import React from 'react'

import { Section, SectionHeading, Card, Button } from '../../../../components/ui'
import { bookingPath, enterBooking } from '../../../../lib/booking-entry'
import { WASH_DESCRIPTORS, WASH_LABELS } from '../../../../lib/wash-copy'
import { LARGE_CAR_WORDS } from '../../../../lib/car-size-copy'
import { readWashes } from '../../../../lib/page-prices'

import './whats-included.css'

// SLOT 7.3 - WHAT'S INCLUDED. Design direction §7.3.
//
// Objection it answers: "which one do I want?"
//
// =====================================================================
// WHAT THIS REBUILD IS FOR
// =====================================================================
//
// The owner's two notes on the previous version were "the colors are a bit
// samey or dull?" and a question about the "swipe mobile experience". A first
// answer stepped three typefaces through the price and stepped the card
// padding 20 / 24 / 32; he called the result "corrupted" and he was right.
// Both were reverted. The lesson that governs this file: a device that needs
// explaining is not deliberate, it is a defect. So there is still no eleventh
// colour in here (DESIGN.md §1) and there is no clever lever either. What
// replaced them is plainer and does more work:
//
//   1. THE WORDS DO THE SEPARATING, AT A SIZE YOU CAN READ. The contract says
//      the washes differ "by words and price, not by colour". The previous
//      build obeyed the letter of that and then set those words in 13px
//      --mute, the weakest type on the card, under a 28px price. The
//      descriptor and the same-price line are now --fs-body and --fs-label in
//      --ink. Nothing else changed about them. They are simply allowed to be
//      seen.
//
//   2. THE SAME-PRICE SENTENCE, GENERATED, NOT WRITTEN. See below. This is the
//      one real new idea in the file and it is the fix for the problem the
//      palette was hiding.
//
//   3. EQUAL FRAMES, UNEQUAL CONTENTS. The old row let every card be exactly
//      as tall as its list, so at 1440 the first card stopped 200px above the
//      other two and the three "Book" buttons sat at three different heights.
//      Screenshotted, that is the half-loaded look the padding step was
//      reverted for, arriving by a second route: the primary action appears
//      three times and the eye cannot find the row. The grid now stretches and
//      the buttons sit on one baseline. The 3 / 4 / 8 tick count is still the
//      honest signal and it is EASIER to compare inside a shared frame, which
//      is what a comparison is for. The uneven 0.88 / 1 / 1.12fr widths went
//      with it: three boxes of equal height and slightly unequal width read as
//      a measuring error, not as a decision.
//
//   4. HALF THE CATALOGUE CAME OUT OF A SENTENCE. Full Valet £215, Interior
//      Only £145 and Dog Owners £215 were a single 13px grey line: three of
//      six washes, including both top-of-range products, set in the smallest
//      type in the section. Average order is £125.93, which is above all three
//      cards, so the expensive half of the range was the half being whispered.
//      They are now three bookable rows with their own price, their own line
//      of copy and a 44px target, under the cards and above the notes. A row
//      is not a card, and that difference is the hierarchy: it needs no
//      explaining.
//
//   5. ONE PRICE COLUMN DOWN THE WHOLE RANGE. Cards and rows share the same
//      head layout, name left and price right in the same face at the same
//      size, ordered by price ascending. So all six prices land on one right
//      edge and read as one list: 69, 69, 95, 145, 215, 215. That ordering is
//      also what puts each same-price pair next to its twin.
//
// =====================================================================
// WHY THE COPY READS LIKE A VALETER AND NOT LIKE AN APP
// =====================================================================
//
// Owner note, 4 Oct: the site does not look like a team that can execute,
// "industry standard best guys in the space.. not some tech bros". He is right
// and this section is where it is decided, because an included list is a
// SPECIFICATION and a specification is the most credible thing a trade can
// publish. "Door shuts, door cards, centre console and dash wiped" cannot be
// faked and needs no adjective in front of it. "Interior cleaning" is a
// brochure. The contract bans "premium" by name for exactly this reason: it is
// what gets written when there is no detail. ("Professional" was on that list
// until Robin ruled it allowed on 5 Oct, DESIGN.md §3.1. The point here is
// unchanged: the specification is what persuades, so no adjective is doing the
// work in this section either way.)
//
// So the fix is not to bolt craft vocabulary onto the page. It is to let the
// catalogue's own words carry it and to stop writing around them:
//
//   - The descriptors name the parts, not the benefit. "The paint and the
//     wheels washed by hand, then waxed" rather than "the outside only". "With
//     the pet hair lifted out of the carpets and the seats" rather than "with
//     the pet hair taken out". Same length, and only someone who does the job
//     writes the second one.
//   - The intro carries the two words the owner keeps using, mobile and
//     delivered, as a fact rather than a label: "wherever the car is parked".
//     It does not repeat "our own water and power", which HowItWorks says in
//     the band directly above this one.
//   - The extras line names the two ends of the catalogue, a child seat deep
//     clean and a clay bar and machine polish, both real rows in
//     pages/services/extras/catalogue.js. Naming the detailing end is what
//     says this is a valeting firm and not a car-cleaning app.
//
// WHAT IS DELIBERATELY NOT HERE, and it is the main judgement call in this
// pass. There is real method vocabulary in the repo: frames.js describes
// photographs of snow foam on a Merc, a car under the lance, a gloved hand
// buffing with a microfibre. It is tempting to write "every wash starts with
// snow foam, left to dwell, then a hand wash". Nobody can source that. Those
// are photographs of individual washes by individual self-employed operators,
// and turning them into a standard process claim would be inventing a method,
// which is the one thing the brief rules out. The photographs make that
// argument where they are, in the hero and in RecentWork, by showing it.
//
// WHAT WOULD MAKE THIS SECTION MUCH MORE CONVINCING AND CANNOT BE DONE HERE:
// the included lines themselves are the mirror of pages/services/compare/
// washes.js, so sharpening "Exterior hand wash" into what an operator actually
// does to a panel has to be done in both files at once, by someone who can
// settle what is true across every operator. That is Barry's call and a shared
// data module, not this slot's. In the build report.
//
// =====================================================================
// THE SAME-PRICE PROBLEM, AND THE SENTENCE THAT SOLVES IT
// =====================================================================
//
// Exterior Wash & Wax and Mini Valet are both £69 and they are NOT nested:
// Mini Valet has no wax, so neither is "the other one plus something". Full
// Valet and Dog Owners Valet are both £215 and are not nested either. "Words
// and price" has no price to work with in either pair, and until now the page
// left the visitor to notice the collision and work out the reason alone.
//
// So the page says it, in the one sentence a person behind a counter would
// use: "Same price as Mini Valet, which cleans the inside and skips the wax."
//
// It is reciprocal, it names the twin, and it names what you get instead. A
// shopper reading only one of the two still gets the whole trade.
//
// The sentence is COMPOSED AT RENDER FROM THE PRICES ON THE META TAG, never
// typed out. `twinSentence` compares the formatted price strings across all
// six washes: if two match, both get the sentence; if Barry moves Mini Valet
// to £75 tomorrow, both sentences disappear on the next render with no deploy
// and no stale claim left on the page. A hand-written "Same price as..." would
// have been a lie waiting to happen, which is the same reason no number is
// baked into this bundle.
//
// Each wash carries a `trade` clause for this and nothing else: the half of
// the sentence that describes what IT does that its twin does not. The four
// clauses are deliberately parallel and six words long, so the two halves of a
// pair scan as one choice rather than two descriptions.
//
// =====================================================================
// THE SWIPE, which was the owner's other question. IT IS GONE, ON EVIDENCE.
// =====================================================================
//
// The rail worked, in the sense that it scrolled. Screenshot it at 390 and the
// problem is not subtle: one card fills the measure and 46px of the next one
// shows, so a visitor sees "Exterior Wash & Wax £69" and nothing else. The two
// £69 prices can never be on screen at the same time. The single most
// important fact in the section is the one the phone layout makes it
// impossible to perceive.
//
// A rail is right for browsing and wrong for comparing. The hero's filmstrip
// stays a rail because photographs are browsed. This is a price list, so below
// 960px it is a stacked column with the prices aligned on one right edge: the
// eye runs straight down 69 / 69 / 95 / 145 / 215 / 215 and the whole range is
// readable with a thumb. Nothing is hidden behind a gesture and nothing needs
// to be discovered.
//
// It costs height, and height below the fold is the cheapest thing this page
// has to spend. Two mitigations keep it honest: the name and price share one
// line instead of two, and between 600 and 959px the included list runs in two
// columns rather than leaving half a wide card empty.
//
// =====================================================================
// THE ADD-ONS: THE DISCLOSURE IS GONE TOO
// =====================================================================
//
// There are 28 extras. The previous build put six of them behind a <details>.
// Six names out of 28, with no prices attached, is the worst of both: it
// raises the question it cannot answer, because add-on prices are not on the
// meta tag (MetaTags::Helper::FOLD_PRICES publishes the six washes and two
// surcharges and nothing else) and an XHR in this section is exactly what the
// rebuild exists to remove.
//
// The extras are also not a decision this section needs to help with. They are
// chosen inside the booking form, each one priced as it is ticked. What a
// visitor needs here is the fact, not the inventory, so it is one line in the
// notes with two concrete examples so it is not abstract, and the full list is
// a link. 31 paid campaigns aimed at those niches returned 12 conversions from
// 1,759 clicks (0.7%), so the long tail is not what people arrive for.
//
// =====================================================================
// "BEFORE YOU BOOK", and what it deliberately does NOT say
// =====================================================================
//
// 88.5% of the people who open the booking form never pay. This section cannot
// fix the form, but it owns one of the reasons: price surprise. A visitor who
// reads £69 here and meets £69 + £20 + £18 there has been ambushed. Those two
// surcharges were already on the page and they were the last and greyest line
// in the section. They are now a named block at body size, next to the prices
// they modify, with both amounts read from the same meta tag as the washes.
//
// What the block does NOT repeat: anything about when the card is charged.
// The hero's trust line says it, the hero subhead says it, and HowItWorks step
// 2 says it in the section immediately above this one. A fourth time on one
// page is noise, not reassurance.
//
// WHAT IS MISSING AND CANNOT BE INVENTED: how long each wash takes. It is the
// single most useful fact this section still cannot state, it is a property of
// the wash so it belongs here and not in HowItWorks, and there is no duration
// anywhere in the catalogue, the seeds, the FAQ or llms.txt. A guess would be
// a promise an operator has to keep. Flagged in the build report.
//
// =====================================================================
// WHAT COMES FROM WHERE
// =====================================================================
//
// Prices: the `gwmc:prices` meta tag the layout writes on every render
// (app/services/meta_tags/helper.rb FOLD_PRICES). Never an XHR, because the
// old useLivePrice path put /api/v2/services in front of the LCP, and never a
// literal, because client/gwmc/store/constants/prices.js once carried a
// 420-line copy still claiming Mini Valet £49. A price we do not have yet is a
// skeleton, never a guess.
//
// Included lists: row for row the same data as pages/services/compare/washes.js,
// so the home page and /services cannot claim different things. Counts come out
// 3, 4 and 8, which that file's own comment asserts. It is a mirror and not an
// import because importing across the two would drag a slot's stylesheet into
// the other's chunk; the fix is a shared data module, which is a file outside
// this directory. Flagged in the build report.

// Reading the meta. Third copy of these five lines (sections/hero/index.js and
// lib/page-prices.js has the others). lib/page-prices.js is the home of it as
// of 5 October 2026; this copy has not been folded into it yet.
const readPrices = () => {
  if (typeof document === 'undefined') return {}
  const tag = document.querySelector("meta[name='gwmc:prices']")
  try {
    return JSON.parse(tag?.getAttribute('content') || '{}')
  } catch (e) {
    return {}
  }
}

// The three washes on cards, price ascending.
//
// `descriptor` is what the wash is. `trade` is what it does that a wash at the
// same price does not, and it is only ever read by the twin's sentence, so
// Mini Valet PLUS has none: at £95 it has no twin to be told apart from.
//
// Mini Valet PLUS's descriptor is the one claim in here that the old build
// could not make. It used to say "no 'everything in the last one, plus'
// wording would be true", and for either £69 wash alone that is right. It is
// wrong for the two of them together: check the rows in
// pages/services/compare/washes.js and Mini Valet PLUS contains all three of
// Exterior Wash & Wax's lines and all four of Mini Valet's, 5 unique, and then
// adds interior glass, the screenwash check and the gutters for 8. So the
// sentence is true, it is the only true nesting statement in the catalogue,
// and it is the reason to spend £26 more.
const TIERS = [
  {
    slug: 'exterior_wash_and_wax',
    name: 'Exterior Wash & Wax',
    descriptor: WASH_DESCRIPTORS.exterior_wash_and_wax,
    trade: 'waxes the paint and skips the inside',
    includes: ['Exterior hand wash', 'Wheels cleaned, tyres dressed', 'Basic body wax']
  },
  {
    slug: 'mini_valet',
    name: 'Mini Valet',
    descriptor: WASH_DESCRIPTORS.mini_valet,
    trade: 'cleans the inside and skips the wax',
    includes: [
      'Exterior hand wash',
      'Wheels cleaned, tyres dressed',
      'Seats, carpet, mats and boot vacuumed',
      'Door shuts, door cards, centre console and dash wiped'
    ]
  },
  {
    slug: 'mini_valet_plus',
    name: 'Mini Valet PLUS',
    descriptor: WASH_DESCRIPTORS.mini_valet_plus,
    label: WASH_LABELS.mini_valet_plus,
    includes: [
      'Exterior hand wash',
      'Wheels cleaned, tyres dressed',
      'Basic body wax',
      'Seats, carpet, mats and boot vacuumed',
      'Door shuts, door cards, centre console and dash wiped',
      'Interior windows cleaned',
      'Screenwash refill, oil and fluid check',
      'Boot and bonnet gutters cleaned'
    ]
  }
]

// The other three washes, price ascending, as rows rather than cards. A fourth
// and fifth and sixth card would flatten the section into a matrix, which is
// the thing this design replaced; a row is visibly secondary and still carries
// a price, a line of copy and a 44px target into the booking flow.
const OTHERS = [
  {
    slug: 'interior_only_valet',
    name: 'Interior Only Valet',
    descriptor: WASH_DESCRIPTORS.interior_only_valet
  },
  {
    slug: 'full_valet',
    name: 'Full Valet',
    descriptor: WASH_DESCRIPTORS.full_valet,
    trade: 'does the engine bay and a hard wax coating'
  },
  {
    slug: 'dog_owners_valet',
    name: 'Dog Owners Valet',
    descriptor: WASH_DESCRIPTORS.dog_owners_valet,
    trade: 'lifts the pet hair out instead'
  }
]

// Every wash on the page, so a same-price twin can be found across the card
// row and the list below it. Full Valet and Dog Owners Valet are both rows;
// the two £69 washes are both cards. The lookup does not care which.
const ALL_WASHES = [...TIERS, ...OTHERS]

// The sentence that fixes the same-price pairs, composed from the meta tag.
//
// Two washes are twins when the server formatted their prices to the same
// string. Nothing is hardcoded: change a price in the admin and the sentence
// appears or disappears on the next render. Returns null when a wash has no
// twin, which is the common case and the whole reason this is generated rather
// than written into the copy.
const twinSentence = (wash, prices) => {
  const mine = prices[wash.slug]
  if (!mine) return null

  const twins = ALL_WASHES.filter(other => other.slug !== wash.slug && prices[other.slug] === mine)
  if (twins.length === 0) return null

  const names = twins.map(twin => twin.name).join(' and ')
  const clause = twins.length === 1 && twins[0].trade ? `, which ${twins[0].trade}` : ''

  return `Same price as ${names}${clause}.`
}

// A missing price is a blank of the right size, never a number that might be
// wrong. One face and one size for all six, cards and rows alike: the same
// amount rendered two ways in one list reads as a rendering fault, which is
// what the reverted three-typeface build proved.
const Price = ({ value }) =>
  value ? (
    <span className="included-price">{value}</span>
  ) : (
    <span className="included-price included-price--pending" aria-hidden="true" />
  )

const Tick = () => <span className="included-tick" aria-hidden="true" />

// The wash's own page (/services/<slug>), off gwmc:catalogue like the prices.
// A plain link: the page is server-rendered and loads as a document.
const pagePath = slug => readWashes().find(wash => wash.slug === slug)?.path

const Wash = ({ wash, price, twin, lead }) => (
  <Card as="article" className="included-wash">
    <div className="included-wash-head">
      <h3 className="included-wash-name">
        {pagePath(wash.slug) ? (
          <a className="included-wash-page" href={pagePath(wash.slug)}>
            {wash.name}
          </a>
        ) : (
          wash.name
        )}
        {wash.label && <span className="gwmc-tag">{wash.label}</span>}
      </h3>
      <Price value={price} />
    </div>

    {/* Descriptor and same-price sentence are one block so the hairline under
        them can be reserved once and land at the same height on all three
        cards. Reserving each line separately left Mini Valet PLUS, which has
        no twin, with its rule 48px above its neighbours', and a rule that
        moves between neighbours is the half-loaded look again. */}
    <div className="included-wash-copy">
      <p className="included-wash-descriptor">{wash.descriptor}</p>
      {twin && <p className="included-wash-twin">{twin}</p>}
    </div>

    {/* The hairline separates the argument for the wash from the inventory of
        it. One rule per card, in the same place on all three, because the
        reverted build taught that an element that moves between neighbours
        reads as a page that has half loaded. */}
    <p className="included-wash-count">Included ({wash.includes.length})</p>
    <ul className="included-wash-list">
      {wash.includes.map(item => (
        <li className="included-wash-item" key={item}>
          <Tick />
          <span>{item}</span>
        </li>
      ))}
    </ul>

    {/* href AND handler, on every bookable tile: the href is what a crawler, a
        middle click and a long press follow, the handler is what keeps the SPA
        from reloading the bundle.

        The label is the same three words on all three cards. It used to carry
        the wash name, which wrapped "Book Exterior Wash & Wax" onto two lines
        in a 288px card and made the three buttons three different shapes. The
        card above it already says which wash this is; the screen reader gets
        it from aria-label and PostHog gets it from the slug on the event. */}
    <Button
      className="included-wash-book"
      variant={lead ? 'primary' : 'secondary'}
      block
      href={bookingPath(wash.slug)}
      aria-label={`Book ${wash.name}`}
      onClick={event => {
        event.preventDefault()
        enterBooking(wash.slug, 'included_section_button')
      }}>
      Book now
    </Button>
    {/* More before committing: the wash's own page (Robin, 25 Oct 2026). */}
    {pagePath(wash.slug) && (
      <a className="gwmc-link included-wash-info" href={pagePath(wash.slug)}>
        See what&apos;s included<span className="gwmc-sr-only"> in the {wash.name}</span>
      </a>
    )}
  </Card>
)

// A row is no longer one link (Robin, 25 Oct 2026: "if it says book now they
// might expect the booking form, but if they want more info they want the
// dedicated page"): the name and "See what's included" open the wash's page,
// "Book now" the form on that wash. Both 44px tall.
const OtherWash = ({ wash, price, twin }) => {
  const page = pagePath(wash.slug) || '/services'
  return (
    <li className="included-row">
      <div className="included-row-link">
        <span className="included-wash-head">
          <a className="included-row-name included-wash-page" href={page}>
            {wash.name}
          </a>
          <Price value={price} />
        </span>
        <span className="included-row-descriptor">{wash.descriptor}</span>
        {twin && <span className="included-row-twin">{twin}</span>}
        <span className="included-row-go">
          <a className="gwmc-link included-row-info" href={page}>
            See what&apos;s included<span className="gwmc-sr-only"> in the {wash.name}</span>
          </a>
          <a
            className="gwmc-btn gwmc-btn--secondary included-row-book"
            href={bookingPath(wash.slug)}
            onClick={event => {
              event.preventDefault()
              enterBooking(wash.slug, 'included_other_wash')
            }}>
            Book now<span className="gwmc-sr-only"> {wash.name}</span>
          </a>
        </span>
      </div>
    </li>
  )
}

// Honest pricing on the page beats a surprise on the booking screen (§7.3),
// and 88.5% of the people who open that screen never pay. Both amounts are
// surcharge rows in the same catalogue as the washes and arrive on the same
// meta tag, so neither is typed here. If the server ever stops publishing one,
// the fact is still true without the number rather than going stale with it.
const notes = prices => [
  {
    lead: 'Car size',
    body: prices.large_car
      ? `The prices above are for a hatchback, saloon or estate. A large car (${LARGE_CAR_WORDS}) adds ${prices.large_car}.`
      : `The prices above are for a hatchback, saloon or estate. A large car (${LARGE_CAR_WORDS}) adds a fixed amount, shown before you pay.`
  },
  {
    lead: 'Congestion Zone',
    body: prices.congestion_zone
      ? `An address inside it adds ${prices.congestion_zone}.`
      : 'An address inside it adds a fixed amount, shown before you pay.'
  },
  {
    lead: 'Extras',
    body: 'Add extras to any wash, from a child seat deep clean to a clay bar and machine polish.'
  },
  {
    lead: 'Need it today?',
    body: 'Same-day washes available.'
  }
]

export const WhatsIncluded = () => {
  const prices = readPrices()

  return (
    <Section className="included">
      {/* No kit specifics (2 Nov 2026): "fresh microfibre" was a method claim
          nobody has sourced, and the rule now is to sell the outcome and the
          proof (the photographs, the wash count, the valeter) instead. */}
      <SectionHeading intro="Every wash is done by hand by a professional valeter, wherever your car is parked.">
        Pick your wash
      </SectionHeading>

      <div className="included-grid">
        {TIERS.map((tier, index) => (
          <Wash
            key={tier.slug}
            wash={tier}
            price={prices[tier.slug]}
            twin={twinSentence(tier, prices)}
            lead={index === TIERS.length - 1}
          />
        ))}
      </div>

      <div className="included-rest">
        <h3 className="included-rest-title">The other three washes</h3>

        <ul className="included-rest-list">
          {OTHERS.map(wash => (
            <OtherWash key={wash.slug} wash={wash} price={prices[wash.slug]} twin={twinSentence(wash, prices)} />
          ))}
        </ul>

        <p className="included-rest-more">
          <a className="gwmc-link" href="/services#compare">
            Compare all washes
          </a>
        </p>
      </div>

      <Card className="included-notes">
        <h3 className="included-notes-title">Good to know</h3>
        <ul className="included-notes-list">
          {notes(prices).map(note => (
            <li className="included-notes-item" key={note.lead}>
              <span className="included-notes-lead">{/[.?!]$/.test(note.lead) ? note.lead : `${note.lead}.`}</span>{' '}
              {note.body}
            </li>
          ))}
        </ul>
      </Card>
    </Section>
  )
}
